import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Sparkles, Send, X, Minimize2, Maximize2, Wand2, Search, FileText,
  Loader2, Check, XCircle, Bot, User, ChevronDown, Figma, Globe,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Quick action definitions ─────────────────────────────────────────────────

const QUICK_ACTIONS = [
  {
    id: 'generate',
    label: 'Generate Page',
    icon: Sparkles,
    prompt: null, // requires user input
    placeholder: 'Describe the page you want to build...',
  },
  {
    id: 'add_section',
    label: 'Add Section',
    icon: Wand2,
    prompt: null,
    placeholder: 'What kind of section? (e.g., testimonials, pricing, FAQ)',
  },
  {
    id: 'improve_copy',
    label: 'Improve Copy',
    icon: FileText,
    prompt: 'Improve the copy on this page to be more compelling and conversion-focused',
  },
  {
    id: 'seo_check',
    label: 'SEO Check',
    icon: Search,
    prompt: '__seo__',
  },
  {
    id: 'figma_import',
    label: 'Import Figma',
    icon: Figma,
    prompt: '__figma__',
  },
  {
    id: 'url_import',
    label: 'Clone URL',
    icon: Globe,
    prompt: '__url_import__',
  },
];

// ─── Message bubble ───────────────────────────────────────────────────────────

function MessageBubble({ message }) {
  if (message.type === 'action') {
    return (
      <div className="flex justify-center py-1.5">
        <span className="text-[11px] text-stone-400 bg-stone-100 px-3 py-1 rounded-full">
          {message.text}
        </span>
      </div>
    );
  }

  if (message.type === 'seo') {
    return (
      <div className="flex justify-start">
        <div className="max-w-[92%] rounded-xl bg-white border border-stone-200 p-3 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Search className="w-3.5 h-3.5 text-stone-500" />
            <span className="text-xs font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              SEO Analysis
            </span>
            {message.score != null && (
              <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${
                message.score >= 80 ? 'bg-green-100 text-green-700'
                  : message.score >= 50 ? 'bg-amber-100 text-amber-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {message.score}/100
              </span>
            )}
          </div>
          {message.suggestions && message.suggestions.length > 0 && (
            <ul className="space-y-1.5">
              {message.suggestions.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-stone-600">
                  {s.done
                    ? <Check className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                    : <XCircle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                  }
                  <span>{s.text}</span>
                </li>
              ))}
            </ul>
          )}
          {message.text && (
            <p className="text-xs text-stone-500 mt-2">{message.text}</p>
          )}
        </div>
      </div>
    );
  }

  const isUser = message.type === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] flex gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isUser ? 'bg-stone-800' : 'bg-gradient-to-br from-violet-500 to-indigo-600'
        }`}>
          {isUser
            ? <User className="w-3 h-3 text-white" />
            : <Bot className="w-3 h-3 text-white" />
          }
        </div>
        <div className={`rounded-xl px-3 py-2 text-sm leading-relaxed ${
          isUser
            ? 'bg-stone-800 text-white'
            : 'bg-white border border-stone-200 text-stone-700 shadow-sm'
        }`}>
          <p className="whitespace-pre-wrap">{message.text}</p>
          {message.pendingBlocks && (
            <div className="mt-2 pt-2 border-t border-stone-200/40">
              <p className="text-[11px] text-stone-400 mb-1.5">
                {message.pendingBlocks.length} block{message.pendingBlocks.length !== 1 ? 's' : ''} ready
              </p>
              <div className="flex gap-1.5">
                <button
                  onClick={message.onApply}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                >
                  <Check className="w-3 h-3" /> Apply Changes
                </button>
                <button
                  onClick={message.onDiscard}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-stone-200 text-stone-600 hover:bg-stone-300 transition-colors"
                >
                  <X className="w-3 h-3" /> Discard
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Typing indicator ─────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="flex gap-2">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Bot className="w-3 h-3 text-white" />
        </div>
        <div className="rounded-xl px-4 py-3 bg-white border border-stone-200 shadow-sm flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AIBuilderPanel({
  siteId,
  pageId,
  blocks,
  selectedBlockId,
  onBlocksUpdate,
  getAuthHeader,
  isOpen: externalIsOpen,
  onClose,
  onFigmaImport,
  onURLImport,
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Sync with external open state
  useEffect(() => {
    if (externalIsOpen !== undefined) setIsOpen(externalIsOpen);
  }, [externalIsOpen]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      type: 'assistant',
      text: 'Hi! I can help you build and refine your page. Try a quick action above or tell me what you\'d like to create.',
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { id, placeholder }

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when panel opens or action selected
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized, pendingAction]);

  // ─── Message helpers ────────────────────────────────────────────────────────

  const addMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, { id: Date.now().toString() + Math.random(), ...msg }]);
  }, []);

  const updateMessage = useCallback((id, updates) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...updates } : m)));
  }, []);

  // ─── API calls ──────────────────────────────────────────────────────────────

  const callGeneratePage = async (prompt) => {
    const headers = getAuthHeader();
    const res = await axios.post(`${API}/sites/${siteId}/ai/generate-page`, {
      prompt,
      page_id: pageId,
    }, { headers });
    return res.data;
  };

  const callEditBlocks = async (prompt, extraBlocks) => {
    const headers = getAuthHeader();
    const res = await axios.post(`${API}/sites/${siteId}/ai/edit-blocks`, {
      prompt,
      blocks: extraBlocks || blocks,
      selected_block_id: selectedBlockId || null,
    }, { headers });
    return res.data;
  };

  const callSuggestSeo = async () => {
    const headers = getAuthHeader();
    const res = await axios.post(`${API}/sites/${siteId}/ai/suggest-seo`, {
      blocks,
    }, { headers });
    return res.data;
  };

  // ─── Handle send ────────────────────────────────────────────────────────────

  const handleSend = async (overrideText) => {
    const text = overrideText || input.trim();
    if (!text || isLoading) return;

    const actionId = pendingAction?.id;
    setPendingAction(null);
    setInput('');

    // Add user message
    addMessage({ type: 'user', text });
    setIsLoading(true);

    try {
      if (actionId === 'seo_check' || text === '__seo__') {
        // SEO check doesn't need user text input
        const data = await callSuggestSeo();
        addMessage({
          type: 'seo',
          text: data.summary || '',
          score: data.score ?? null,
          suggestions: (data.suggestions || []).map((s) =>
            typeof s === 'string' ? { text: s, done: false } : s
          ),
        });
      } else if (actionId === 'generate' || (!actionId && blocks.length === 0)) {
        // Generate a full page
        const data = await callGeneratePage(text);
        const newBlocks = data.blocks || [];
        const msgId = Date.now().toString();
        addMessage({
          id: msgId,
          type: 'assistant',
          text: data.summary || `Generated ${newBlocks.length} block${newBlocks.length !== 1 ? 's' : ''} for your page.`,
          pendingBlocks: newBlocks,
          onApply: () => {
            onBlocksUpdate(newBlocks);
            updateMessage(msgId, { pendingBlocks: null });
            addMessage({ type: 'action', text: 'Changes applied to canvas' });
          },
          onDiscard: () => {
            updateMessage(msgId, { pendingBlocks: null });
            addMessage({ type: 'action', text: 'Changes discarded' });
          },
        });
      } else {
        // Edit existing blocks (add section, improve copy, general edits)
        const data = await callEditBlocks(text);
        const newBlocks = data.blocks || [];
        const msgId = Date.now().toString();
        addMessage({
          id: msgId,
          type: 'assistant',
          text: data.summary || `Updated ${newBlocks.length} block${newBlocks.length !== 1 ? 's' : ''}.`,
          pendingBlocks: newBlocks,
          onApply: () => {
            onBlocksUpdate(newBlocks);
            updateMessage(msgId, { pendingBlocks: null });
            addMessage({ type: 'action', text: 'Changes applied to canvas' });
          },
          onDiscard: () => {
            updateMessage(msgId, { pendingBlocks: null });
            addMessage({ type: 'action', text: 'Changes discarded' });
          },
        });
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.message || 'Something went wrong';
      toast.error(`AI error: ${errMsg}`);
      addMessage({
        type: 'assistant',
        text: `Sorry, I ran into an error: ${errMsg}. Please try again.`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Quick action handler ───────────────────────────────────────────────────

  const handleQuickAction = (action) => {
    if (isLoading) return;

    if (action.id === 'figma_import') {
      onFigmaImport?.();
      return;
    }

    if (action.id === 'url_import') {
      onURLImport?.();
      return;
    }

    if (action.id === 'seo_check') {
      addMessage({ type: 'user', text: 'Run an SEO check on this page' });
      setIsLoading(true);
      callSuggestSeo()
        .then((data) => {
          addMessage({
            type: 'seo',
            text: data.summary || '',
            score: data.score ?? null,
            suggestions: (data.suggestions || []).map((s) =>
              typeof s === 'string' ? { text: s, done: false } : s
            ),
          });
        })
        .catch((err) => {
          const errMsg = err.response?.data?.detail || err.message || 'Something went wrong';
          toast.error(`AI error: ${errMsg}`);
          addMessage({
            type: 'assistant',
            text: `Sorry, I couldn't complete the SEO check: ${errMsg}`,
          });
        })
        .finally(() => setIsLoading(false));
      return;
    }

    if (action.id === 'improve_copy') {
      handleSend(action.prompt);
      return;
    }

    // For generate / add_section, set pending action to customize the input placeholder
    setPendingAction({ id: action.id, placeholder: action.placeholder });
    inputRef.current?.focus();
  };

  // ─── Key handler ────────────────────────────────────────────────────────────

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ─── Toggle button (always visible) ─────────────────────────────────────────

  if (!isOpen) {
    // When controlled externally, don't show FAB
    if (externalIsOpen !== undefined) return null;
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
        title="AI Builder"
      >
        <Sparkles className="w-5 h-5" />
      </button>
    );
  }

  // ─── Minimized state ────────────────────────────────────────────────────────

  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50 w-[400px] rounded-xl border border-stone-200 bg-white shadow-2xl overflow-hidden">
        <div
          className="flex items-center justify-between px-4 py-2.5 bg-stone-900 text-white cursor-pointer"
          onClick={() => setIsMinimized(false)}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              AI Builder
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}
              className="p-1 hover:bg-white/10 rounded transition-colors"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setIsOpen(false); setIsMinimized(false); onClose?.(); }}
              className="p-1 hover:bg-white/10 rounded transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Full panel ─────────────────────────────────────────────────────────────

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[400px] h-[520px] rounded-xl border border-stone-200 bg-[#FAFAF5] shadow-2xl flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-stone-900 text-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            AI Builder
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title="Minimize"
          >
            <Minimize2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => { setIsOpen(false); setIsMinimized(false); onClose?.(); }}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-3 py-2.5 border-b border-stone-200 flex-shrink-0">
        <div className="flex flex-wrap gap-1.5">
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={() => handleQuickAction(action)}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-lg border border-stone-200 bg-white text-stone-600 hover:bg-stone-50 hover:border-stone-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon className="w-3 h-3" />
                {action.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        {isLoading && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Pending action hint */}
      {pendingAction && (
        <div className="px-3 py-1.5 bg-violet-50 border-t border-violet-100 flex items-center justify-between flex-shrink-0">
          <span className="text-[11px] text-violet-600 font-medium">
            {pendingAction.id === 'generate' ? 'Generating page' : 'Adding section'} — describe what you need
          </span>
          <button
            onClick={() => setPendingAction(null)}
            className="text-violet-400 hover:text-violet-600 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Input area */}
      <div className="px-3 py-2.5 border-t border-stone-200 flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={pendingAction?.placeholder || 'Type a message...'}
            disabled={isLoading}
            rows={1}
            className="flex-1 resize-none px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 disabled:opacity-50 placeholder:text-stone-400"
            style={{ maxHeight: '80px', minHeight: '36px' }}
          />
          <Button
            size="sm"
            onClick={() => handleSend()}
            disabled={isLoading || !input.trim()}
            className="h-9 w-9 p-0 bg-stone-900 hover:bg-stone-800 text-white rounded-lg flex-shrink-0"
          >
            {isLoading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Send className="w-4 h-4" />
            }
          </Button>
        </div>
      </div>
    </div>
  );
}
